// Small ACT! 4.0-style field building blocks used by ContactCardPage.
// Extracted from the main page so ContactCardPage.js stays focused on
// orchestration/state. Each of these components is small, pure, and
// depends only on its props — safe to memoize if we ever need to.

import { useState, useEffect, useRef } from 'react';

// A single labeled text/number/whatever input, styled to look like a
// classic ACT! field row (right-aligned label · thin border · dense).
export function ActField({ label, value, onChange, placeholder, type = 'text', className = '', testId, trailing }) {
  return (
    <div className={`flex items-center gap-1 ${className}`}>
      <label className="text-[11px] text-[#333] w-20 shrink-0 text-right pr-1 font-medium">{label}</label>
      <div className="flex-1 relative">
        <input
          type={type}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoComplete="one-time-code"
          name={`act_${testId}_${Math.random()}`}
          className={`w-full h-[26px] px-1.5 ${trailing ? 'pr-7' : ''} text-[12px] bg-white border border-[#B0B0B0] rounded-none focus:outline-none focus:border-[#0B5394] focus:ring-1 focus:ring-[#0B5394]/30`}
          data-testid={testId}
        />
        {trailing && (
          <div className="absolute right-0.5 top-1/2 -translate-y-1/2">{trailing}</div>
        )}
      </div>
    </div>
  );
}

// Combined "Contact" field that displays `First Last` but writes back to
// separate first_name / last_name via onChange(first, last).
//
// Uses local state for the display string so typing a space doesn't get
// swallowed by re-render trimming (this was a real bug — see PRD v2.53.0).
export function ContactNameField({ firstName, lastName, onChange, testId }) {
  const [local, setLocal] = useState(
    `${firstName || ''}${(firstName && lastName) ? ' ' : ''}${lastName || ''}`
  );
  const propsKey = `${firstName}\u0001${lastName}`;
  const lastSyncedKey = useRef(propsKey);

  // If the underlying contact changes (navigate to a different record), refresh
  // the display from props. Otherwise leave the user's in-progress typing alone.
  useEffect(() => {
    if (lastSyncedKey.current !== propsKey) {
      setLocal(`${firstName || ''}${(firstName && lastName) ? ' ' : ''}${lastName || ''}`);
      lastSyncedKey.current = propsKey;
    }
  }, [propsKey, firstName, lastName]);

  const handleChange = (v) => {
    setLocal(v);
    // First space wins. Trailing-space tolerance: "Bob " keeps first="Bob"
    // and preserves the trailing space in the display.
    const idx = v.indexOf(' ');
    const first = idx === -1 ? v : v.slice(0, idx);
    const last = idx === -1 ? '' : v.slice(idx + 1);
    lastSyncedKey.current = `${first}\u0001${last}`;
    onChange(first, last);
  };

  return (
    <div className="flex items-center gap-1">
      <label className="text-[11px] text-[#333] w-20 shrink-0 text-right pr-1 font-medium">Contact</label>
      <input
        type="text"
        value={local}
        onChange={(e) => handleChange(e.target.value)}
        autoComplete="one-time-code"
        name={`act_${testId}_${Math.random()}`}
        className="flex-1 h-[26px] px-1.5 text-[12px] bg-white border border-[#B0B0B0] rounded-none focus:outline-none focus:border-[#0B5394] focus:ring-1 focus:ring-[#0B5394]/30"
        data-testid={testId}
      />
    </div>
  );
}

// Native <select> styled to match ActField.
export function ActSelect({ label, value, onChange, options, testId }) {
  return (
    <div className="flex items-center gap-1">
      <label className="text-[11px] text-[#333] w-20 shrink-0 text-right pr-1 font-medium">{label}</label>
      <select
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className="flex-1 h-[26px] px-1 text-[12px] bg-white border border-[#B0B0B0] rounded-none focus:outline-none focus:border-[#0B5394]"
        data-testid={testId}
      >
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

// ACT! 4.0-style USER-defined custom field. Admin picks the type
// (text/dropdown/checkbox/date/number); this component renders the
// right input for that type.
export function ActUserField({ field, value, onChange, testId }) {
  const type = field.type || 'text';
  const labelEl = (
    <label
      className="text-[11px] text-[#333] w-20 shrink-0 text-right pr-1 font-medium truncate"
      title={field.label}
    >
      {field.label}
    </label>
  );

  if (type === 'dropdown') {
    const opts = Array.isArray(field.options) ? field.options : [];
    const valueNotInOptions = value && !opts.includes(value);
    return (
      <div className="flex items-center gap-1">
        {labelEl}
        <select
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 h-[26px] px-1 text-[12px] bg-white border border-[#B0B0B0] rounded-none focus:outline-none focus:border-[#0B5394]"
          data-testid={testId}
        >
          <option value="">—</option>
          {opts.map((o) => <option key={o} value={o}>{o}</option>)}
          {valueNotInOptions && <option value={value}>{value} (legacy)</option>}
        </select>
      </div>
    );
  }

  if (type === 'checkbox') {
    const checked = value === true || value === 'true' || value === 'yes' || value === 1 || value === '1';
    return (
      <div className="flex items-center gap-1">
        {labelEl}
        <div className="flex-1 flex items-center h-[26px] pl-1">
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => onChange(e.target.checked ? 'yes' : '')}
            className="w-4 h-4 accent-[#0B5394]"
            data-testid={testId}
          />
          <span className="ml-2 text-[11px] text-[#666]">{checked ? 'Yes' : 'No'}</span>
        </div>
      </div>
    );
  }

  if (type === 'date') {
    return (
      <div className="flex items-center gap-1">
        {labelEl}
        <input
          type="date"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 h-[26px] px-1.5 text-[12px] bg-white border border-[#B0B0B0] rounded-none focus:outline-none focus:border-[#0B5394]"
          data-testid={testId}
        />
      </div>
    );
  }

  if (type === 'number') {
    return (
      <div className="flex items-center gap-1">
        {labelEl}
        <input
          type="number"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 h-[26px] px-1.5 text-[12px] bg-white border border-[#B0B0B0] rounded-none focus:outline-none focus:border-[#0B5394] focus:ring-1 focus:ring-[#0B5394]/30"
          data-testid={testId}
        />
      </div>
    );
  }

  // default: text
  return (
    <div className="flex items-center gap-1">
      {labelEl}
      <input
        type="text"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        autoComplete="one-time-code"
        name={`act_${testId}_${Math.random()}`}
        className="flex-1 h-[26px] px-1.5 text-[12px] bg-white border border-[#B0B0B0] rounded-none focus:outline-none focus:border-[#0B5394] focus:ring-1 focus:ring-[#0B5394]/30"
        data-testid={testId}
      />
    </div>
  );
}
